// ========================================
// Planificación mensual (RRHH y personal). Replaces RRHH's spreadsheet of two sheets:
// · «Altas y bajas»: the month calendar with every hire (and its end date, whether the
//   advisory was told, FRA for freelancers) and every end of contract.
// · «Por persona»: one row per person and subject, one cell per day, coloured by subject on
//   teaching days, with «time · subject · group» on hover.
// Both come from contracts, classes and approved leaves. On top, RRHH writes her own notes
// and colours on any day or cell (GET/PUT /api/staff-plan/notes).
// ========================================
const StaffPlanView = {
    _month: null,      // YYYY-MM
    _tab: 'altas',     // 'altas' | 'people'
    _notes: new Map(), // cellKey → note
    _leaves: [],
    _canWrite: false,
    _sel: null,        // { date, professorId, subjectId }
    _open: new Set(),  // professor ids unfolded in «Por persona»
    _clip: null,       // { fill, font } copied with «Copiar formato»
    _request: 0,

    FILLS: { red: ['Rojo', '#E53935', '#FFFFFF'], grey: ['Gris', '#BDBDBD', '#0F1B19'], yellow: ['Amarillo', '#FFF176', '#0F1B19'], pink: ['Rosa', '#F8A5DE', '#0F1B19'], cyan: ['Cian', '#4DD0E1', '#0F1B19'] },
    FONTS: { pink: ['Rosa', '#C0158F'], red: ['Rojo', '#B4483C'] },

    canView() { return AuthManager.isAdmin() || AuthManager.isRRHH() || AuthManager.hasModule('planificacion-mensual') || AuthManager.hasModule('personal-laboral'); },
    _pad(n) { return String(n).padStart(2, '0'); },
    _iso(y, m, d) { return `${y}-${this._pad(m + 1)}-${this._pad(d)}`; },
    _esc(v) { return Utils.sanitizeHTML(String(v ?? '')); },
    _key(date, professorId, subjectId) { return `${date}|${professorId || '-'}|${subjectId || '-'}`; },
    _parts() { const [y, m] = this._month.split('-').map(Number); return { y, m: m - 1, days: new Date(y, m, 0).getDate() }; },
    _dow(iso) { return new Date(`${iso}T12:00:00`).getDay(); },
    _isOff(iso) { const d = this._dow(iso); return d === 0 || d === 6 || (typeof DataManager.isHoliday === 'function' && !!DataManager.isHoliday(iso)); },
    _short(iso) { const [y, m, d] = String(iso || '').split('-'); return d ? `${d}/${m}/${y}` : ''; },
    _name(p) { return `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Sin nombre'; },

    render() {
        if (!this.canView()) return '<div class="view-container" role="alert">No tienes acceso a esta sección.</div>';
        if (!this._month) { const now = new Date(); this._month = `${now.getFullYear()}-${this._pad(now.getMonth() + 1)}`; }
        return `
        <div class="view-container dnr-proposal pmn-view" style="padding-top: 1rem;">
            <header class="pmn-top">
                <h1 class="pmn-title">Planificación mensual</h1>
                <div class="pmn-month">
                    <button type="button" aria-label="Mes anterior" ${UIEvents.attr("click", function(event,captured){StaffPlanView.changeMonth(-1);}, [])}><i class="fas fa-chevron-left"></i></button>
                    <span id="pmn-month-label"></span>
                    <button type="button" aria-label="Mes siguiente" ${UIEvents.attr("click", function(event,captured){StaffPlanView.changeMonth(1);}, [])}><i class="fas fa-chevron-right"></i></button>
                </div>
                <div class="pmn-seg" id="pmn-tabs" role="tablist" aria-label="Pestaña"></div>
                <span class="pmn-spacer"></span>
                <span id="pmn-pills" class="pmn-pills"></span>
            </header>
            <div class="pmn-layout">
                <div id="pmn-body"><p class="pmn-muted">Cargando…</p></div>
                <aside class="pmn-card pmn-panel" id="pmn-panel"></aside>
            </div>
        </div>`;
    },

    async init() {
        if (!this.canView()) return;
        this._sel = null;
        await this.load();
    },

    async load() {
        const request = ++this._request;
        const { y, m, days } = this._parts();
        const from = this._iso(y, m, 1), to = this._iso(y, m, days);
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) FileStorage.needClasses(from, to, () => { if (request === this._request) this.paint(); });
        this.paint();
        const [notes, leaves] = await Promise.all([
            fetch(`/api/staff-plan/notes?month=${this._month}`, { cache: 'no-store' }).then(r => r.json()).catch(() => null),
            fetch(`/api/staff-leaves/overview?from=${from}&to=${to}`, { cache: 'no-store' }).then(r => r.ok ? r.json() : null).catch(() => null)
        ]);
        if (request !== this._request) return;
        this._notes = new Map(((notes && notes.success && notes.notes) || []).map(n => [this._key(n.date, n.professorId, n.subjectId), n]));
        this._canWrite = !!(notes && notes.canWrite);
        this._leaves = (leaves && leaves.success && leaves.away) || [];
        this.paint();
    },

    changeMonth(delta) {
        const { y, m } = this._parts();
        const d = new Date(y, m + delta, 1);
        this._month = `${d.getFullYear()}-${this._pad(d.getMonth() + 1)}`;
        this._sel = null;
        this.load();
    },
    setTab(tab) { this._tab = tab === 'people' ? 'people' : 'altas'; this._sel = null; this.paint(); },
    select(date, professorId, subjectId) { this._sel = { date, professorId: professorId || null, subjectId: subjectId || null }; this.paint(); },

    // Every class session of the month: single dates, or weekly slots on their weekday.
    _sessions() {
        const { y, m, days } = this._parts();
        const from = this._iso(y, m, 1), to = this._iso(y, m, days);
        const out = [];
        DataManager.getClasses().forEach(cls => {
            if (!cls.startDate) return;
            const subject = DataManager.getSubject(cls.subjectId);
            const professorId = cls.professorId || subject?.professorId;
            if (!professorId) return;
            const start = cls.startDate, end = cls.endDate && cls.endDate > cls.startDate ? cls.endDate : cls.startDate;
            if (start > to || end < from) return;
            const push = date => out.push({ cls, subject, professorId: String(professorId), subjectId: cls.subjectId ? String(cls.subjectId) : null, date });
            if (start === end) { push(start); return; }
            for (let d = 1; d <= days; d++) { const iso = this._iso(y, m, d); if (iso >= start && iso <= end && this._dow(iso) === Number(cls.dayOfWeek)) push(iso); }
        });
        return out;
    },

    _contractsInMonth(person) {
        const { y, m, days } = this._parts();
        const from = this._iso(y, m, 1), to = this._iso(y, m, days);
        return StaffPay.contractsOf(person).filter(c => c.startDate <= to && (!c.endDate || c.endDate >= from));
    },

    // Hires and ends of contract inside the month.
    _moves() {
        const { y, m, days } = this._parts();
        const from = this._iso(y, m, 1), to = this._iso(y, m, days);
        const out = [];
        DataManager.getProfessors().forEach(p => StaffPay.contractsOf(p).forEach(c => {
            if (c.startDate >= from && c.startDate <= to) out.push({ kind: 'alta', date: c.startDate, p, c });
            if (c.endDate && c.endDate >= from && c.endDate <= to) out.push({ kind: 'baja', date: c.endDate, p, c });
        }));
        return out.sort((a, b) => a.date.localeCompare(b.date));
    },

    _altaText(c) {
        return `${c.endDate ? `hasta ${this._short(c.endDate)}` : 'sin fecha de fin'} ${c.freelance ? 'FRA' : c.advisoryNotified ? '✓' : '⚠'}`;
    },

    _noteStyle(note, base = '') {
        if (!note) return base;
        const fill = note.fill && this.FILLS[note.fill];
        return `${base}${fill ? `background: ${fill[1]}; color: ${fill[2]};` : ''}${note.font ? `color: ${this.FONTS[note.font][1]};` : ''}`;
    },

    paint() {
        this.closeMenu();
        const body = document.getElementById('pmn-body');
        if (!body) return;
        const { y, m } = this._parts();
        const label = new Date(y, m, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
        document.getElementById('pmn-month-label').textContent = label.charAt(0).toUpperCase() + label.slice(1);
        SafeHTML.set(document.getElementById('pmn-tabs'), [['altas', 'Altas y bajas'], ['people', 'Por persona']].map(([id, text]) =>
            `<button type="button" role="tab" class="${this._tab === id ? 'is-on' : ''}" aria-selected="${this._tab === id}" ${UIEvents.attr("click", function(event,captured){StaffPlanView.setTab(String(captured[0]));}, [id])}>${text}</button>`).join(''));
        const moves = this._moves();
        const altas = moves.filter(x => x.kind === 'alta'), bajas = moves.filter(x => x.kind === 'baja');
        const untold = altas.filter(x => !x.c.freelance && !x.c.advisoryNotified).length;
        SafeHTML.set(document.getElementById('pmn-pills'), `
            <span class="pmn-pill is-ok">${altas.length} ${altas.length === 1 ? 'alta' : 'altas'}</span>
            <span class="pmn-pill is-bad">${bajas.length} ${bajas.length === 1 ? 'baja' : 'bajas'}</span>
            ${untold ? `<span class="pmn-pill is-warn">${untold} sin avisar a la asesoría</span>` : ''}`);
        SafeHTML.set(body, this._tab === 'people' ? this.renderPeople() : this.renderCalendar(moves));
        this.renderPanel();
    },

    renderCalendar(moves) {
        const { y, m, days } = this._parts();
        const offset = (new Date(y, m, 1).getDay() + 6) % 7;
        const now = new Date(), later = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 15);
        const today = this._iso(now.getFullYear(), now.getMonth(), now.getDate()), limit = this._iso(later.getFullYear(), later.getMonth(), later.getDate());
        const byDay = {};
        moves.forEach(x => { (byDay[x.date] ||= []).push(x); });
        let cells = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => `<span class="pmn-dow">${d}</span>`).join('');
        for (let i = 0; i < offset; i++) cells += '<span class="pmn-day is-blank"></span>';
        for (let d = 1; d <= days; d++) {
            const iso = this._iso(y, m, d);
            const note = this._notes.get(this._key(iso, null, null));
            const sel = this._sel && this._sel.date === iso && !this._sel.professorId;
            // One line per hire or end (full text on hover), at most three; the panel lists them all.
            const list = byDay[iso] || [], shown = list.length > 3 ? list.slice(0, 2) : list;
            const events = shown.map(x => {
                const tail = x.kind === 'alta' ? (x.c.freelance ? 'FRA' : x.c.advisoryNotified ? '✓' : '⚠') : 'BAJA';
                const full = `${this._name(x.p)} · ${x.kind === 'alta' ? 'alta ' + this._altaText(x.c) : 'baja'}`;
                // Hires starting within the next 15 days stand out in yellow.
                const soon = x.kind === 'alta' && x.date >= today && x.date < limit;
                return `<span class="pmn-event is-${x.kind}${soon ? ' is-soon' : ''}" title="${this._esc(full + (soon ? ' · empieza en menos de 15 días' : ''))}"><b>${this._esc(this._name(x.p))}</b><em>${this._esc(tail)}</em></span>`;
            }).join('') + (list.length > shown.length ? `<span class="pmn-more">+${list.length - shown.length} más</span>` : '');
            cells += `<button type="button" class="pmn-day ${this._isOff(iso) ? 'is-off' : ''} ${sel ? 'is-sel' : ''}" style="${this._noteStyle(note && { fill: note.fill })}"
                ${UIEvents.attr("click", function(event,captured){StaffPlanView.select(String(captured[0]));}, [iso])}
                ${UIEvents.attr("contextmenu", function(event,captured){return StaffPlanView.openMenu(event, this, String(captured[0]), null, null);}, [iso])} aria-label="${this._esc(`${d} de ${new Date(y, m, 1).toLocaleDateString('es-ES', { month: 'long' })}`)}">
                <span class="pmn-num">${d}</span>${events}
                ${note && note.text ? `<span class="pmn-day-note" title="${this._esc(note.text)}" style="${note.font ? `color: ${this.FONTS[note.font][1]};` : ''}">${this._esc(note.text)}</span>` : ''}
            </button>`;
        }
        return `<section class="pmn-card"><div class="pmn-cal">${cells}</div></section>
            <p class="pmn-muted pmn-hint">Las altas y bajas salen de los contratos: ✓ asesoría avisada · ⚠ sin avisar · FRA autónomo. Pulsa un día para escribir tu nota o darle color; con el botón derecho salen las opciones.</p>`;
    },

    // One entry per person with a contract or a class in the month, with the subjects they
    // teach. Each group has its own subject record, so subjects with the same name are merged
    // into one row, as in RRHH's sheet.
    _rows(sessions) {
        const people = new Map(DataManager.getProfessors().map(p => [String(p.id), p]));
        const ids = new Set([...DataManager.getProfessors().filter(p => this._contractsInMonth(p).length).map(p => String(p.id)), ...sessions.map(s => s.professorId)]);
        return [...ids].map(id => people.get(id)).filter(Boolean).sort((a, b) => this._name(a).localeCompare(this._name(b), 'es')).map(p => {
            const byName = new Map();
            sessions.filter(s => s.professorId === String(p.id)).forEach(s => {
                const name = (s.subject?.name || '').trim().toLowerCase();
                if (!byName.has(name)) byName.set(name, new Set());
                byName.get(name).add(s.subjectId || null);
            });
            return { p, groups: [...byName.values()].map(set => [...set]) };
        });
    },

    // People start folded: their row sums up every subject; unfolding shows one row per subject.
    toggle(pid) { this._open.has(pid) ? this._open.delete(pid) : this._open.add(pid); this.paint(); },
    toggleAll(open) { this._open = new Set(open ? this._rows(this._sessions()).filter(r => r.groups.length).map(r => String(r.p.id)) : []); this.paint(); },

    _sessionText(s) { return `${s.cls.startTime || ''}${s.cls.endTime ? '–' + s.cls.endTime : ''} · ${s.subject?.name || 'Clase'}${s.cls.formationId && DataManager.getFormation(s.cls.formationId) ? ' · ' + DataManager.getFormation(s.cls.formationId).name : ''}`; },

    // The person row (subjectIds null: every class of the day, ▶/■ marks) or one subject row.
    _cells(p, contracts, sessions, subjectIds, subjectId) {
        const { y, m, days } = this._parts();
        const pid = String(p.id), summary = !subjectIds;
        let cells = '';
        for (let d = 1; d <= days; d++) {
            const iso = this._iso(y, m, d);
            const mine = sessions.filter(s => s.professorId === pid && s.date === iso && (summary || subjectIds.includes(s.subjectId || null)));
            const inContract = !contracts.length ? !!mine.length : contracts.some(c => c.startDate <= iso && (!c.endDate || c.endDate >= iso));
            const leave = this._leaves.find(l => String(l.professorId) === pid && l.startDate <= iso && l.endDate >= iso);
            const note = this._notes.get(this._key(iso, pid, subjectId));
            // Folded, the person row also flags the notes written on its subject rows.
            const flag = (note && note.text) || (summary && !this._open.has(pid) && [...this._notes.values()].some(n => n.date === iso && String(n.professorId) === pid && n.text));
            const start = contracts.some(c => c.startDate === iso), end = contracts.some(c => c.endDate === iso);
            const cls = mine.length ? 'is-class' : leave ? 'is-leave' : this._isOff(iso) ? 'is-off' : inContract ? 'is-on' : '';
            const colors = [...new Set(mine.map(s => s.subject?.color).filter(Boolean))];
            const color = !colors.length ? '' : colors.length === 1 ? `background: ${this._esc(colors[0])};`
                : `background: linear-gradient(to bottom, ${colors.map((c, i) => `${this._esc(c)} ${i * 100 / colors.length}% ${(i + 1) * 100 / colors.length}%`).join(', ')});`;
            const tip = [`${this._name(p)} · ${d}/${m + 1}`, ...mine.map(s => this._sessionText(s)),
                leave ? (leave.kind === 'unpaid' ? 'Permiso' : 'Vacaciones') : '', note && note.text ? `Nota: ${note.text}` : ''].filter(Boolean).join('\n');
            const mark = note && note.fill === 'red' ? '!' : leave ? (leave.kind === 'unpaid' ? 'P' : 'V') : summary && start ? '▶' : summary && end ? '■' : '';
            const sel = this._sel && this._sel.date === iso && this._sel.professorId === pid && (this._sel.subjectId || null) === subjectId;
            cells += `<button type="button" class="pmn-cell ${cls} ${sel ? 'is-sel' : ''}" title="${this._esc(tip)}" aria-label="${this._esc(tip)}" style="${this._noteStyle(note, color)}"
                ${UIEvents.attr("click", function(event,captured){StaffPlanView.select(String(captured[0]),String(captured[1]),captured[2]);}, [iso, pid, subjectId])}
                ${UIEvents.attr("contextmenu", function(event,captured){return StaffPlanView.openMenu(event, this, String(captured[0]), String(captured[1]), captured[2]);}, [iso, pid, subjectId])}>${mark}${flag ? '<i class="pmn-flag" aria-hidden="true"></i>' : ''}</button>`;
        }
        return cells;
    },

    renderPeople() {
        const { y, m, days } = this._parts();
        const sessions = this._sessions();
        const rows = this._rows(sessions);
        const W = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
        const allOpen = rows.some(r => r.groups.length) && rows.filter(r => r.groups.length).every(r => this._open.has(String(r.p.id)));
        let head = `<span class="pmn-who"><button type="button" class="pmn-fold-all" ${UIEvents.attr("click", function(event,captured){StaffPlanView.toggleAll(!captured[0]);}, [allOpen])}>${allOpen ? 'Plegar todo' : 'Desplegar todo'}</button></span>`;
        for (let d = 1; d <= days; d++) { const iso = this._iso(y, m, d); head += `<span class="pmn-hd ${this._isOff(iso) ? 'is-off' : ''}">${W[this._dow(iso)]}<br>${d}</span>`; }
        const body = rows.map(({ p, groups }) => {
            const pid = String(p.id);
            const contracts = this._contractsInMonth(p);
            const freelance = contracts.some(c => c.freelance);
            const open = this._open.has(pid);
            const subjects = groups.map(ids => ids[0] ? DataManager.getSubject(ids[0]) : null);
            const sub = !groups.length ? (contracts.length ? 'Sin clases este mes' : '') : groups.length === 1 ? (subjects[0]?.name || 'Clase') : `${groups.length} asignaturas`;
            const name = `<b>${this._esc(this._name(p))}${freelance ? ' <span class="pmn-fra">FRA</span>' : ''}</b><small>${this._esc(sub)}</small>`;
            const menu = UIEvents.attr("contextmenu", function(event,captured){return StaffPlanView.openPersonMenu(event, this, String(captured[0]));}, [pid]);
            const who = groups.length
                ? `<button type="button" class="pmn-who pmn-fold" aria-expanded="${open}" title="${open ? 'Plegar' : 'Ver sus asignaturas'}" ${UIEvents.attr("click", function(event,captured){StaffPlanView.toggle(String(captured[0]));}, [pid])} ${menu}>
                    <i class="fas fa-chevron-${open ? 'down' : 'right'}" aria-hidden="true"></i><span>${name}</span></button>`
                : `<span class="pmn-who" ${menu}><span>${name}</span></span>`;
            const lines = open ? groups.map((ids, i) => `<div class="pmn-row is-sub">
                <span class="pmn-who" style="${subjects[i]?.color ? `border-left: 6px solid ${this._esc(subjects[i].color)};` : ''}"><small>${this._esc(subjects[i]?.name || 'Clase')}</small></span>${this._cells(p, contracts, sessions, ids, ids[0] || null)}</div>`).join('') : '';
            return `<div class="pmn-row is-person">${who}${this._cells(p, contracts, sessions, null, null)}</div>${lines}`;
        }).join('');
        return `<section class="pmn-card"><div class="pmn-grid-scroll"><div class="pmn-grid" style="--pmn-days: ${days};">
                <div class="pmn-row is-head">${head}</div>${body || '<p class="pmn-muted" style="padding: 1rem;">Nadie tiene contrato ni clases este mes.</p>'}
            </div></div></section>
            <p class="pmn-muted pmn-hint">Pulsa el nombre para ver sus asignaturas; con el botón derecho, sobre una casilla o un nombre, salen las opciones. Cada casilla de color es un día de clase con el color de su asignatura (si da varias ese día, salen todas); al pasar el ratón sale el horario, la asignatura y el grupo. ▶ alta · ■ baja · V vacaciones · P permiso. La esquina roja indica que hay nota.</p>`;
    },

    renderPanel() {
        const panel = document.getElementById('pmn-panel');
        if (!panel) return;
        const sel = this._sel;
        if (!sel) {
            SafeHTML.set(panel, `<div class="pmn-panel-body"><span class="pmn-label">Notas de RRHH</span><p class="pmn-muted">Pulsa ${this._tab === 'people' ? 'una casilla' : 'un día'} para ver lo que dice SICAF y escribir tu nota o darle color.</p></div>`);
            return;
        }
        const person = sel.professorId ? DataManager.getProfessors().find(p => String(p.id) === sel.professorId) : null;
        const subject = sel.subjectId ? DataManager.getSubject(sel.subjectId) : null;
        const note = this._notes.get(this._key(sel.date, sel.professorId, sel.subjectId)) || {};
        const dayLabel = new Date(`${sel.date}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        let auto = [];
        if (person) {
            // The person row (no subject) lists every class of the day.
            const sameName = s => !sel.subjectId || (s.subject?.name || '').trim().toLowerCase() === (subject?.name || '').trim().toLowerCase();
            auto = this._sessions().filter(s => s.professorId === sel.professorId && s.date === sel.date && sameName(s)).map(s => this._sessionText(s));
            const leave = this._leaves.find(l => String(l.professorId) === sel.professorId && l.startDate <= sel.date && l.endDate >= sel.date);
            if (leave) auto.push(leave.kind === 'unpaid' ? 'Permiso sin sueldo' : 'Vacaciones');
            const c = StaffPay.contractOn(person, sel.date);
            auto.push(c ? `Contratado${c.endDate ? ` hasta el ${this._short(c.endDate)}` : ''}${c.freelance ? ' · autónomo (FRA)' : ''}` : 'Sin contrato ese día');
        } else {
            auto = this._moves().filter(x => x.date === sel.date).map(x => `${this._name(x.p)} · ${x.kind === 'alta' ? 'alta ' + this._altaText(x.c) : 'baja'}`);
            if (this._isOff(sel.date)) auto.push('Fin de semana o festivo');
        }
        const can = this._canWrite;
        const swatch = (id, [name, bg, fg]) => `<button type="button" class="pmn-swatch ${note.fill === id ? 'is-on' : ''}" data-fill="${id}" aria-pressed="${note.fill === id}" title="${name}" aria-label="Casilla ${name.toLowerCase()}" style="background: ${bg}; color: ${fg};" ${can ? '' : 'disabled'}
            ${UIEvents.attr("click", function(event,captured){StaffPlanView.pickFill(String(captured[0]));}, [id])}>${id === 'red' ? '!' : ''}</button>`;
        SafeHTML.set(panel, `
            <div class="pmn-panel-head">
                <span class="pmn-label">${person ? 'Casilla' : 'Día'}</span>
                <h2>${this._esc(person ? this._name(person) : dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1))}</h2>
                ${person ? `<small>${this._esc(dayLabel)}${subject ? ' · ' + this._esc(subject.name) : ''}</small>` : ''}
            </div>
            <div class="pmn-panel-body">
                ${auto.length ? `<div class="pmn-auto"><span class="pmn-label">De SICAF</span>${auto.map(a => `<span>${this._esc(a)}</span>`).join('')}</div>` : ''}
                <label class="pmn-field">Tu nota<textarea id="pmn-note-text" rows="4" maxlength="500" placeholder="Ej.: avión cancelado, se mirarán fechas" ${can ? '' : 'disabled'}>${this._esc(note.text || '')}</textarea></label>
                <div><span class="pmn-label">Color de la casilla</span>
                    <div class="pmn-swatches" id="pmn-fills">
                        <button type="button" class="pmn-swatch is-none ${!note.fill ? 'is-on' : ''}" data-fill="" aria-pressed="${!note.fill}" title="Sin color" aria-label="Sin color" ${can ? '' : 'disabled'} ${UIEvents.attr("click", function(event,captured){StaffPlanView.pickFill('');}, [])}></button>
                        ${Object.entries(this.FILLS).map(([id, v]) => swatch(id, v)).join('')}
                    </div>
                </div>
                <div><span class="pmn-label">Color de la letra</span>
                    <div class="pmn-swatches" id="pmn-fonts">
                        ${[['', 'Normal', '#0F1B19'], ['pink', 'Rosa', this.FONTS.pink[1]], ['red', 'Rojo', this.FONTS.red[1]]].map(([id, name, color]) => `<button type="button" class="pmn-font ${(note.font || '') === id ? 'is-on' : ''}" data-font="${id}" aria-pressed="${(note.font || '') === id}" style="color: ${color};" ${can ? '' : 'disabled'}
                            ${UIEvents.attr("click", function(event,captured){StaffPlanView.pickFont(String(captured[0]));}, [id])}>${name}</button>`).join('')}
                    </div>
                </div>
                ${can ? `<div class="pmn-actions">
                    <button type="button" class="pmn-btn is-primary" ${UIEvents.attr("click", function(event,captured){StaffPlanView.save();}, [])}>Guardar</button>
                    ${note.id ? `<button type="button" class="pmn-btn is-danger" ${UIEvents.attr("click", function(event,captured){StaffPlanView.clear();}, [])}>Quitar nota y colores</button>` : ''}
                </div>` : '<p class="pmn-muted">Solo consulta.</p>'}
            </div>`);
    },

    pickFill(id) { document.querySelectorAll('#pmn-fills .pmn-swatch').forEach(b => { const on = b.dataset.fill === id; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on)); }); },
    pickFont(id) { document.querySelectorAll('#pmn-fonts .pmn-font').forEach(b => { const on = b.dataset.font === id; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', String(on)); }); },

    async _put(payload) {
        const token = AuthManager.getCsrfToken();
        const res = await fetch('/api/staff-plan/notes', { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': token }, body: JSON.stringify(payload) });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data || !data.success) throw new Error((data && data.error) || 'No se pudo guardar la nota.');
        return data.note;
    },

    // Saves a cell's note (an empty one without colours is removed on the server).
    async _store(cell, fields, toast) {
        try {
            const note = await this._put({ date: cell.date, professorId: cell.professorId, subjectId: cell.subjectId, ...fields });
            const key = this._key(cell.date, cell.professorId, cell.subjectId);
            if (note) this._notes.set(key, note); else this._notes.delete(key);
            Utils.showToast(toast || (note ? 'Nota guardada' : 'Nota quitada'), 'success');
            this.paint();
        } catch (e) {
            Utils.showToast(e.message, 'error');
        }
    },

    async save(empty = false) {
        if (!this._sel || !this._canWrite) return;
        return this._store(this._sel, {
            text: empty ? '' : (document.getElementById('pmn-note-text')?.value || ''),
            fill: empty ? '' : (document.querySelector('#pmn-fills .pmn-swatch.is-on')?.dataset.fill || ''),
            font: empty ? '' : (document.querySelector('#pmn-fonts .pmn-font.is-on')?.dataset.font || '') });
    },
    clear() { return this.save(true); },

    // ─── Right-click menu, like Excel's: colours, note and copy/paste format of a cell or day;
    // fold, profile and contract on a name. Closes on any click outside, Escape, scroll or repaint.
    _menuCell: null,

    _showMenu(event, anchor, html) {
        event.preventDefault();
        this.closeMenu();
        const menu = document.createElement('div');
        menu.id = 'pmn-menu';
        menu.className = 'pmn-menu';
        menu.setAttribute('role', 'menu');
        SafeHTML.set(menu, html);
        document.body.appendChild(menu);
        // From the keyboard (context-menu key) there is no pointer position: open by the element.
        const box = anchor.getBoundingClientRect();
        const x = event.clientX || box.left + box.width / 2, y = event.clientY || box.bottom;
        const w = menu.offsetWidth || 240, h = menu.offsetHeight || 300;
        menu.style.left = `${Math.max(8, Math.min(x, window.innerWidth - w - 8))}px`;
        menu.style.top = `${Math.max(8, Math.min(y, window.innerHeight - h - 8))}px`;
        if (!this._menuBound) {
            this._menuBound = true;
            document.addEventListener('mousedown', e => { if (!e.target.closest?.('#pmn-menu')) this.closeMenu(); });
            document.addEventListener('keydown', e => { if (e.key === 'Escape') this.closeMenu(); });
            window.addEventListener('scroll', () => this.closeMenu(), true);
            window.addEventListener('resize', () => this.closeMenu());
        }
        menu.querySelector('button:not([disabled])')?.focus();
        return false;
    },

    closeMenu() { document.getElementById('pmn-menu')?.remove(); },

    _item(text, fn, args = [], extra = '') {
        return `<button type="button" role="menuitem" class="pmn-menu-item ${extra}" ${UIEvents.attr("click", fn, args)}>${text}</button>`;
    },

    openMenu(event, anchor, date, professorId, subjectId) {
        const cell = this._menuCell = { date, professorId: professorId || null, subjectId: subjectId || null };
        const note = this._notes.get(this._key(cell.date, cell.professorId, cell.subjectId));
        const person = cell.professorId ? DataManager.getProfessors().find(p => String(p.id) === cell.professorId) : null;
        const label = new Date(`${date}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
        const head = `<div class="pmn-menu-head">${this._esc(person ? `${this._name(person)} · ${label}` : label)}</div>`;
        if (!this._canWrite) {
            return this._showMenu(event, anchor, head + this._item('Ver en el panel', function(){StaffPlanView.menuNote();}) + '<p class="pmn-menu-off">Solo consulta.</p>');
        }
        const fills = [['', 'Sin color', 'transparent', '#0F1B19'], ...Object.entries(this.FILLS).map(([id, [name, bg, fg]]) => [id, name, bg, fg])];
        const swatches = fills.map(([id, name, bg, fg]) => `<button type="button" role="menuitemradio" aria-checked="${(note?.fill || '') === id}" class="pmn-swatch ${id ? '' : 'is-none'} ${(note?.fill || '') === id ? 'is-on' : ''}" title="${name}" aria-label="Casilla ${name.toLowerCase()}" style="${id ? `background: ${bg}; color: ${fg};` : ''}"
            ${UIEvents.attr("click", function(event,captured){StaffPlanView.menuFormat({ fill: String(captured[0]) });}, [id])}>${id === 'red' ? '!' : ''}</button>`).join('');
        const fonts = [['', 'Normal', '#0F1B19'], ['pink', 'Rosa', this.FONTS.pink[1]], ['red', 'Rojo', this.FONTS.red[1]]].map(([id, name, color]) =>
            `<button type="button" role="menuitemradio" aria-checked="${(note?.font || '') === id}" class="pmn-font ${(note?.font || '') === id ? 'is-on' : ''}" style="color: ${color};"
            ${UIEvents.attr("click", function(event,captured){StaffPlanView.menuFormat({ font: String(captured[0]) });}, [id])}>${name}</button>`).join('');
        const clip = this._clip;
        return this._showMenu(event, anchor, `${head}
            <div class="pmn-menu-group"><span class="pmn-label">Color de la casilla</span><div class="pmn-swatches">${swatches}</div></div>
            <div class="pmn-menu-group"><span class="pmn-label">Color de la letra</span><div class="pmn-swatches">${fonts}</div></div>
            <div class="pmn-menu-sep"></div>
            ${this._item(`<i class="fas fa-pen" aria-hidden="true"></i> ${note?.text ? 'Editar nota' : 'Escribir nota'}`, function(){StaffPlanView.menuNote();})}
            ${this._item('<i class="fas fa-copy" aria-hidden="true"></i> Copiar formato', function(){StaffPlanView.menuCopy();})}
            ${clip ? this._item('<i class="fas fa-paste" aria-hidden="true"></i> Pegar formato', function(){StaffPlanView.menuFormat({ ...StaffPlanView._clip });}) : ''}
            ${note ? `<div class="pmn-menu-sep"></div>${this._item('<i class="fas fa-eraser" aria-hidden="true"></i> Quitar nota y colores', function(){StaffPlanView.menuFormat({ text: '', fill: '', font: '' });}, [], 'is-danger')}` : ''}`);
    },

    // Changes the menu's cell keeping what is not touched (a colour keeps the note and vice versa).
    menuFormat(change) {
        const cell = this._menuCell;
        this.closeMenu();
        if (!cell || !this._canWrite) return;
        const note = this._notes.get(this._key(cell.date, cell.professorId, cell.subjectId)) || {};
        const fields = { text: note.text || '', fill: note.fill || '', font: note.font || '', ...change };
        this._sel = cell;
        return this._store(cell, fields, fields.text || fields.fill || fields.font ? 'Casilla guardada' : 'Nota quitada');
    },

    menuCopy() {
        const cell = this._menuCell;
        this.closeMenu();
        if (!cell) return;
        const note = this._notes.get(this._key(cell.date, cell.professorId, cell.subjectId)) || {};
        this._clip = { fill: note.fill || '', font: note.font || '' };
        Utils.showToast('Formato copiado: pulsa con el botón derecho en otra casilla y «Pegar formato».', 'info');
    },

    menuNote() {
        const cell = this._menuCell;
        this.closeMenu();
        if (!cell) return;
        this.select(cell.date, cell.professorId, cell.subjectId);
        const text = document.getElementById('pmn-note-text');
        if (text && !text.disabled) { text.focus(); text.setSelectionRange?.(text.value.length, text.value.length); }
    },

    // The contract in force this month (else the last one of the month).
    _monthContract(person) {
        const list = this._contractsInMonth(person);
        const { y, m, days } = this._parts();
        const now = new Date(), today = this._iso(now.getFullYear(), now.getMonth(), now.getDate());
        const ref = today >= this._iso(y, m, 1) && today <= this._iso(y, m, days) ? today : this._iso(y, m, days);
        return list.find(c => c.startDate <= ref && (!c.endDate || c.endDate >= ref)) || list[list.length - 1] || null;
    },

    openPersonMenu(event, anchor, pid) {
        const person = DataManager.getProfessors().find(p => String(p.id) === pid);
        if (!person) return;
        const hasSubjects = this._rows(this._sessions()).some(r => String(r.p.id) === pid && r.groups.length);
        const contract = this._monthContract(person);
        const manage = typeof ProfessorProfile !== 'undefined' && ProfessorProfile.canManageStaff && ProfessorProfile.canManageStaff();
        const canContract = contract && (contract.documentFilePath || (manage && contract.id));
        return this._showMenu(event, anchor, `<div class="pmn-menu-head">${this._esc(this._name(person))}</div>
            ${hasSubjects ? this._item(`<i class="fas fa-chevron-${this._open.has(pid) ? 'up' : 'down'}" aria-hidden="true"></i> ${this._open.has(pid) ? 'Plegar sus asignaturas' : 'Desplegar sus asignaturas'}`, function(event,captured){StaffPlanView.closeMenu();StaffPlanView.toggle(String(captured[0]));}, [pid]) : ''}
            ${this._item('<i class="fas fa-id-card" aria-hidden="true"></i> Abrir su ficha', function(event,captured){StaffPlanView.closeMenu();App.navigate('profesor/' + String(captured[0]));}, [pid])}
            ${canContract ? this._item('<i class="fas fa-file-signature" aria-hidden="true"></i> Ver su contrato', function(event,captured){StaffPlanView.menuContract(String(captured[0]));}, [pid]) : ''}`);
    },

    // The signed PDF if there is one; otherwise the contract's details (for those who manage staff).
    menuContract(pid) {
        this.closeMenu();
        const person = DataManager.getProfessors().find(p => String(p.id) === pid);
        const contract = person && this._monthContract(person);
        if (!contract) return;
        if (contract.documentFilePath) {
            const date = new Date(`${contract.startDate}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
            return ContractDocs.view(contract.documentFilePath, `Contrato · ${this._name(person)} · ${date}`);
        }
        if (contract.id) ProfessorProfile.openContractForm(pid, contract.id);
    }
};

window.StaffPlanView = StaffPlanView;
